import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { getFullName } from '../../../utils/get-initials';
import { styles } from './styles';
import Overview from './Overview';
import { useViewAccountDetailsMutation, useUpdateAccountDetailsMutation } from '../../../api/account';

const Account = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { id } = useParams();
    const user = useSelector((state) => state.session.user);
    const [userDisplayData, setUserDisplayData] = useState();
    const fullName = useCallback(() => {
        return getFullName(user?.firstName, user?.middleName, user?.lastName);
    }, [user?.firstName, user?.middleName, user?.lastName]);

    const location = useLocation();

    const [isEditMode, setIsEditMode] = useState(false);

    const [viewAccountDetails, { isLoading: isViewLoading }] = useViewAccountDetailsMutation();
    const [updateAccountDetails, { isLoading: isUpdateLoading }] = useUpdateAccountDetailsMutation();

    const isLoading = isViewLoading || isUpdateLoading;

    useEffect(() => {
        if (id && location.pathname.includes('/account/update')) {
            setIsEditMode(true);
        } else {
            setIsEditMode(false);
        }
    }, [id]);

    const fetchUserData = async () => {
        try {
            const apiPath = isEditMode ? updateAccountDetails : viewAccountDetails;
            const response = await apiPath().unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            setUserDisplayData(response);
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else
                enqueueSnackbar(err?.message || 'Something went wrong! Account Data could not be fetched', {
                    variant: 'error',
                });
        }
    };

    useEffect(() => {
        async function fetchUser() {
            await fetchUserData();
        }
        fetchUser();
    }, [user.userDBId, isEditMode]);

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">{`${userDisplayData?.secUserInstance?.employee?.firstName} ${userDisplayData?.secUserInstance?.employee?.lastName}`}</Typography>
                {!isEditMode && (
                    <Button
                        color="primary"
                        onClick={() => navigate(`/account/update/${user.userDBId}`)}
                        size="large"
                        startIcon={<EditIcon fontSize="small" />}
                        variant="outlined"
                    >
                        Update profile
                    </Button>
                )}
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Overview
                    fetchUserData={fetchUserData}
                    isEditMode={isEditMode}
                    user={userDisplayData}
                    isLoading={isLoading}
                    fullName={fullName()}
                />
            </Box>
        </Box>
    );
};

export default Account;
